import { userIdSchema } from "@estate-platform/domain/ids";
import { isAuthApiError } from "@supabase/auth-js";

import { ceremonyOutcome, codeOutcome, verifyTotp } from "./ceremony.ts";
import { readClaims } from "./claims.ts";
import { functionOutcome } from "./sign-in-link.ts";

import type { CeremonyOutcome, CodeOutcome } from "./ceremony.ts";
import type { ApiClient } from "./client.ts";
import type { LinkOutcome } from "./sign-in-link.ts";
import type { UserId } from "@estate-platform/domain/ids";
import type { Locale } from "@estate-platform/i18n/runtime";

export interface SignedInUser {
  readonly id: UserId;
  readonly email: string;
  readonly sessionId: string;
  readonly isComplete: boolean;
  readonly verifiedUntil: number | null;
  readonly language: Locale | null;
}

type VerifyOutcome = "expired" | "verified";
type SignOutReason = "security" | "user";

export interface Auth {
  readonly currentUser: () => Promise<SignedInUser | null>;
  readonly requestLink: (email: string, language: Locale) => Promise<LinkOutcome>;
  readonly verifyLink: (tokenHash: string) => Promise<VerifyOutcome>;
  readonly signInWithPasskey: () => Promise<CeremonyOutcome>;
  readonly hasCode: () => Promise<boolean>;
  readonly verifyCode: (code: string) => Promise<CodeOutcome>;
  readonly signOut: (reason?: SignOutReason) => Promise<void>;
  readonly onSignedOut: (listener: (reason: SignOutReason) => void) => () => void;
  readonly lastSignOutReason: () => SignOutReason | null;
  readonly onSessionChange: (listener: () => void) => () => void;
  readonly refreshSession: () => Promise<void>;
}

const unknownAccountCodes: ReadonlySet<string> = new Set([
  "otp_disabled",
  "signup_disabled",
  "user_not_found",
]);

const directLink = async (client: ApiClient, email: string): Promise<LinkOutcome> => {
  const { error } = await client.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
  if (error === null || (isAuthApiError(error) && unknownAccountCodes.has(error.code ?? ""))) {
    return "sent";
  }
  return error.status === 429 ? "rateLimited" : "failed";
};

const currentUserOf = (client: ApiClient) => async (): Promise<SignedInUser | null> => {
  const { data, error } = await client.auth.getClaims();
  if (error !== null) {
    throw error;
  }
  if (data === null) {
    return null;
  }
  const { claims } = data;
  return { id: userIdSchema.parse(claims.sub), email: claims.email ?? "", ...readClaims(claims) };
};

const verifiedCodeFactor = async (client: ApiClient): Promise<string | null> => {
  const { data, error } = await client.auth.mfa.listFactors();
  if (error !== null) {
    throw error;
  }
  return data.totp[0]?.id ?? null;
};

const passkeySignIn =
  (client: ApiClient, currentUser: Auth["currentUser"]) => async (): Promise<CeremonyOutcome> => {
    const previous = await currentUser();
    const { error } = await client.auth.signInWithPasskey();
    const outcome = ceremonyOutcome(error);
    if (outcome === "done" && previous !== null) {
      const { error: replaceError } = await client.db.rpc("replace_session", {
        previous: previous.sessionId,
      });
      if (replaceError !== null) {
        throw replaceError;
      }
    }
    return outcome;
  };

// WHY: a step-up taken in another tab lands in the next token; refreshing reads it before asking again.
const refreshSessionOf = (client: ApiClient) => async (): Promise<void> => {
  const { error } = await client.auth.refreshSession();
  if (error !== null) {
    throw error;
  }
};

export const createAuth = (client: ApiClient): Auth => {
  const currentUser = currentUserOf(client);
  const listeners = new Set<(reason: SignOutReason) => void>();
  const sessionListeners = new Set<() => void>();
  let pendingReason: SignOutReason | null = null;
  let lastReason: SignOutReason | null = null;
  const takeSignOutReason = (): SignOutReason => {
    const reason = pendingReason ?? "security";
    pendingReason = null;
    lastReason = reason;
    return reason;
  };
  client.auth.onAuthStateChange((event) => {
    for (const listener of sessionListeners) {
      listener();
    }
    if (event === "SIGNED_IN") {
      lastReason = null;
    }
    if (event !== "SIGNED_OUT") {
      return;
    }
    const reason = takeSignOutReason();
    for (const listener of listeners) {
      listener(reason);
    }
  });
  return {
    currentUser,
    requestLink: async (email, language) => {
      const functions = await client.functions();
      const { data, response } = await functions.invoke<unknown>("request-sign-in", {
        body: { email, language },
      });
      const outcome = functionOutcome(response?.status ?? null, data);
      return outcome === "direct" ? directLink(client, email) : outcome;
    },
    verifyLink: async (tokenHash) => {
      const { error } = await client.auth.verifyOtp({ token_hash: tokenHash, type: "email" });
      if (error === null) {
        return "verified";
      }
      if (isAuthApiError(error)) {
        return "expired";
      }
      throw error;
    },
    signInWithPasskey: passkeySignIn(client, currentUser),
    hasCode: async () => (await verifiedCodeFactor(client)) !== null,
    verifyCode: async (code) => {
      const factorId = await verifiedCodeFactor(client);
      return factorId === null ? "failed" : codeOutcome(await verifyTotp(client.auth.mfa, factorId, code));
    },
    signOut: async (reason = "user") => {
      pendingReason = reason;
      const { error } = await client.auth.signOut({ scope: "local" });
      if (error !== null) {
        throw error;
      }
    },
    onSignedOut: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    lastSignOutReason: () => lastReason,
    onSessionChange: (listener) => {
      sessionListeners.add(listener);
      return () => {
        sessionListeners.delete(listener);
      };
    },
    refreshSession: refreshSessionOf(client),
  };
};
